import { CommonModule, formatNumber } from '@angular/common';
import { Component, ElementRef, Inject, Input, LOCALE_ID, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ApexChart, ApexDataLabels, ApexLegend, ApexNonAxisChartSeries, ApexPlotOptions, ApexTooltip, NgApexchartsModule } from 'ng-apexcharts';
import { ChartColorsConstants } from 'src/app/constants/chart-colors-constants';

@Component({
  selector: 'app-custom-donut-chart',
  templateUrl: './custom-donut-chart.component.html',
  styleUrls: ['./custom-donut-chart.component.scss'],
  standalone: true,
  imports: [CommonModule, NgApexchartsModule]
})
export class CustomDonutChartComponent implements OnInit, OnChanges, OnDestroy {

  @Input() labels: string[] = [];
  @Input() values: number[] = [];
  @Input() colors: string[] = [];
  @Input() total: number | null = null;
  @Input() totalLabel: string = 'Total';
  @Input() height: number = 300;

  chart: ApexChart = { type: 'donut' };
  chartColors: string[] = ChartColorsConstants.defaultColors;
  chartLabels: string[] = [];
  dataLabels: ApexDataLabels = { enabled: false };
  states: ApexStates = { active: { filter: { type: 'none' } } };
  hasData: boolean = false;
  legend: ApexLegend = {};
  legendPosition: 'right' | 'bottom' = 'right';
  plotOptions: ApexPlotOptions = {};
  series: ApexNonAxisChartSeries = [];
  tooltip: ApexTooltip = {};
  private hoveredIndex: number = -1;
  private readonly minSliceRatio: number = 0.02;
  static readonly minWidthLegendRight: number = 300;
  private resizeObserver?: ResizeObserver;

  constructor(@Inject(LOCALE_ID) private locale: string,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(entries => this.onResize(entries[0]?.contentRect.width ?? 0));
      this.resizeObserver.observe(this.elementRef.nativeElement);
    });
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  onResize(width: number) {
    if (!width) return;

    const position = width >= CustomDonutChartComponent.minWidthLegendRight ? 'right' : 'bottom';
    if (position === this.legendPosition) return;

    this.ngZone.run(() => {
      this.legendPosition = position;
      this.legend = this.buildLegend();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const unchanged = Object.values(changes).every(change =>
      !change.firstChange && JSON.stringify(change.previousValue) === JSON.stringify(change.currentValue));
    if (unchanged) return;

    const values = this.values ?? [];
    const sum = values.reduce((acc, value) => acc + value, 0);
    const total = this.total ?? sum;
    const minSlice = sum * this.minSliceRatio;

    this.series = values.map(value => value > 0 ? Math.max(value, minSlice) : value);
    this.hasData = values.some(value => value > 0);
    this.chartLabels = this.labels ?? [];
    this.chartColors = this.colors?.length ? this.colors : ChartColorsConstants.defaultColors;
    this.chart = {
      type: 'donut',
      height: this.height,
      fontFamily: 'Inter, sans-serif',
      toolbar: { show: false },
      animations: { enabled: true },
      events: {
        dataPointMouseEnter: (_event: any, _chart: any, config: any) => {
          this.hoveredIndex = config.dataPointIndex;
        }
      }
    };
    const scale = this.height / 300;
    const labelSize = this.getLabelSize();
    const valueSize = Math.max(16, Math.round(26 * scale));
    this.plotOptions = {
      pie: {
        expandOnClick: false,
        donut: {
          size: '68%',
          labels: {
            show: true,
            name: { fontSize: `${labelSize}px`, color: '#64748b', offsetY: Math.round(-6 * scale) },
            value: { fontSize: `${valueSize}px`, fontWeight: 600, color: '#1e293b', offsetY: Math.round(6 * scale), formatter: (value: string) => this.formatValue(values[this.hoveredIndex] ?? value) },
            total: { show: true, label: this.totalLabel, fontSize: `${labelSize}px`, color: '#64748b', formatter: () => this.formatValue(total) }
          }
        }
      }
    };
    this.legend = this.buildLegend();
    this.tooltip = { y: { formatter: (value: number, opts?: any) => this.formatValue(values[opts?.seriesIndex] ?? value) } };
  }

  private buildLegend(): ApexLegend {
    const values = this.values ?? [];
    const legend: ApexLegend = {
      position: this.legendPosition,
      fontSize: `${this.getLabelSize()}px`,
      formatter: (label: string, opts?: any) => `${label}: ${this.formatValue(values[opts?.seriesIndex] ?? 0)}`
    };
    if (this.legendPosition === 'right') legend.itemMargin = { vertical: 2 };
    return legend;
  }

  private getLabelSize(): number {
    return Math.max(10, Math.round(13 * this.height / 300));
  }

  private formatValue(value: number | string): string {
    return formatNumber(Number(value), this.locale, '1.0-0');
  }
}
